import { useState, useEffect, useRef } from "react";
import { API } from "../App";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  HelpCircle, Search, ArrowLeft, Loader2, ChevronRight,
  ChevronDown, Timer, Target, Filter, BookOpen, Play, Eye
} from "lucide-react";

const DIFFICULTY_COLORS = {
  beginner: "text-emerald-400 bg-emerald-900/20 border-emerald-800/30",
  intermediate: "text-amber-400 bg-amber-900/20 border-amber-800/30",
  advanced: "text-red-400 bg-red-900/20 border-red-800/30",
};

const QAEnginePage = () => {
  const { t } = useTranslation('modules');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // Drill mode
  const [drillMode, setDrillMode] = useState(false);
  const [drillQuestions, setDrillQuestions] = useState([]);
  const [drillIndex, setDrillIndex] = useState(0);
  const [drillAnswers, setDrillAnswers] = useState({});
  const [showAnswer, setShowAnswer] = useState(false);
  const [drillTimer, setDrillTimer] = useState(0);
  const timerRef = useRef(null);

  const fetchQuestions = () => {
    const params = {};
    if (searchQuery) params.q = searchQuery;
    if (categoryFilter) params.category = categoryFilter;
    if (difficultyFilter) params.difficulty = difficultyFilter;
    axios.get(`${API}/eguide/qa`, { params })
      .then(res => { setQuestions(res.data.questions || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchQuestions(); }, [categoryFilter, difficultyFilter]);

  const doSearch = () => { setLoading(true); fetchQuestions(); };

  const startDrill = async () => {
    try {
      const res = await axios.post(`${API}/eguide/qa/drill`, {
        count: 10,
        sector: "all",
        difficulty: difficultyFilter || "all",
      });
      setDrillQuestions(res.data.drill_questions || []);
      setDrillIndex(0);
      setDrillAnswers({});
      setShowAnswer(false);
      setDrillTimer(0);
      setDrillMode(true);
      timerRef.current = setInterval(() => setDrillTimer(t => t + 1), 1000);
    } catch {}
  };

  const revealAnswer = async (qaId) => {
    try {
      const res = await axios.post(`${API}/eguide/qa/check`, { qa_id: qaId });
      setDrillAnswers(prev => ({ ...prev, [qaId]: res.data.correct_answer }));
      setShowAnswer(true);
    } catch {}
  };

  const nextDrillQuestion = () => {
    if (drillIndex < drillQuestions.length - 1) {
      setDrillIndex(drillIndex + 1);
      setShowAnswer(false);
    } else {
      clearInterval(timerRef.current);
    }
  };

  const exitDrill = () => {
    setDrillMode(false);
    clearInterval(timerRef.current);
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /></div>;

  /* ─── Drill Mode ──────────────────────────────────── */
  if (drillMode && drillQuestions.length > 0) {
    const q = drillQuestions[drillIndex];
    const isLast = drillIndex === drillQuestions.length - 1;
    const answered = drillAnswers[q?.qa_id];

    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="drill-mode">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button onClick={exitDrill} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="exit-drill">
            <ArrowLeft className="w-4 h-4" /> Exit Drill
          </button>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-xs text-slate-400"><Timer className="w-3.5 h-3.5" /> {formatTime(drillTimer)}</div>
            <div className="text-xs text-slate-500">{drillIndex + 1} / {drillQuestions.length}</div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-12">
          {/* Progress */}
          <div className="h-1 bg-[#1E3A5F] rounded-full mb-8 overflow-hidden">
            <div className="h-full bg-[#1A6BCC] rounded-full transition-all" style={{ width: `${((drillIndex + (showAnswer ? 1 : 0)) / drillQuestions.length) * 100}%` }} />
          </div>

          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] text-[#00C8E0] uppercase tracking-wider font-bold">{q.category}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFFICULTY_COLORS[q.difficulty]}`}>{q.difficulty}</span>
          </div>

          <h2 className="text-xl font-bold mb-6" data-testid="drill-question">{q.question}</h2>

          {!showAnswer ? (
            <button
              onClick={() => revealAnswer(q.qa_id)}
              className="w-full bg-[#1A6BCC] hover:bg-[#1A6BCC]/80 text-white py-3 rounded font-bold text-sm flex items-center justify-center gap-2"
              data-testid="reveal-answer-btn"
            >
              <Eye className="w-4 h-4" /> Reveal Answer
            </button>
          ) : (
            <div>
              <div className="bg-[#0D1F38] border border-emerald-800/30 rounded-lg p-5 mb-4" data-testid="drill-answer">
                <p className="text-sm text-slate-300 leading-relaxed">{answered}</p>
                <div className="text-[10px] text-slate-500 mt-2">{q.standard}</div>
              </div>
              <button
                onClick={isLast ? exitDrill : nextDrillQuestion}
                className="w-full bg-[#132340] border border-[#1E3A5F] hover:border-[#1A6BCC]/40 text-white py-3 rounded font-bold text-sm flex items-center justify-center gap-2"
                data-testid="next-drill-btn"
              >
                {isLast ? "Finish Drill" : "Next Question"} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ─── Q&A List ──────────────────────────────────── */
  const categories = [...new Set(questions.map(q => q.category))];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="qa-page">
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#1A6BCC] rounded flex items-center justify-center">
              <HelpCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-sm">Q&A Engine</h1>
              <p className="text-[10px] text-slate-500">{questions.length} questions with audit drill mode</p>
            </div>
          </div>
          <button
            onClick={startDrill}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded text-xs font-bold"
            data-testid="start-drill-btn"
          >
            <Play className="w-3.5 h-3.5" /> Drill Mode
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6" data-testid="qa-filters">
          <div className="flex-1 min-w-0 flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search questions..."
              className="flex-1 bg-[#0D1F38] border border-[#1E3A5F] rounded px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none"
              data-testid="qa-search"
            />
            <button onClick={doSearch} className="bg-[#132340] border border-[#1E3A5F] rounded px-3 py-2 text-slate-400 hover:text-white">
              <Search className="w-3.5 h-3.5" />
            </button>
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-2 py-2 text-xs text-white focus:outline-none"
            data-testid="qa-category-filter"
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-2 py-2 text-xs text-white focus:outline-none"
            data-testid="qa-difficulty-filter"
          >
            <option value="">All Levels</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* Questions accordion */}
        <div className="space-y-2" data-testid="qa-list">
          {questions.map(q => (
            <div key={q.qa_id} className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedId(expandedId === q.qa_id ? null : q.qa_id)}
                className="w-full text-left p-4 flex items-start gap-3 hover:bg-[#132340]/50 transition-colors"
                data-testid={`qa-${q.qa_id}`}
              >
                <ChevronDown className={`w-4 h-4 text-slate-500 shrink-0 mt-0.5 transition-transform ${expandedId === q.qa_id ? "rotate-180" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${DIFFICULTY_COLORS[q.difficulty]}`}>{q.difficulty}</span>
                    <span className="text-[10px] text-slate-500">{q.standard}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white">{q.question}</h3>
                </div>
              </button>
              {expandedId === q.qa_id && (
                <div className="px-4 pb-4 pl-11 border-t border-[#1E3A5F]/50" data-testid={`qa-answer-${q.qa_id}`}>
                  <p className="text-sm text-slate-300 leading-relaxed mt-3">{q.answer}</p>
                  <div className="flex items-center gap-2 mt-3 text-[10px] text-slate-500">
                    <span>{q.category}</span>
                    <span className="text-slate-600">|</span>
                    <span>{q.sector}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QAEnginePage;
